import { zodResolver } from "@hookform/resolvers/zod";
import { useLingui } from "@lingui/react";
import { useEffect } from "react";
import type { FieldValues, Mode, UseFormProps } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { z } from "zod/v4";
import { debugLog } from "../../../utils/debug";
import { makeLinguiZodErrorMap } from "./make-lingui-zod-error-map";
import { type ServerError, useServerErrorToFormError } from "./useServerErrorToFormError";

export type UseEnhancedFormProps<TFieldValues extends FieldValues> = {
	initialValues?: UseFormProps<TFieldValues>["defaultValues"] | any;
	mode?: Mode;
	schema: z.ZodType<TFieldValues>;
	serverError?: ServerError | undefined | null;
	t?: unknown;
};

export const useEnhancedForm = <TFieldValues extends FieldValues>({
	initialValues,
	mode = "onBlur",
	schema,
	serverError,
}: UseEnhancedFormProps<TFieldValues>) => {
	const { i18n } = useLingui();
	const methods = useForm<TFieldValues>({
		defaultValues: initialValues,
		mode,
		resolver: schema ? zodResolver(schema as any, { error: makeLinguiZodErrorMap(i18n) } as any) : undefined,
	});

	useServerErrorToFormError({
		serverError,
		setError: methods.setError as any,
	});

	const { errors, isSubmitting, isValid, isDirty } = methods.formState;

	useEffect(() => {
		debugLog("formState", { errors, isSubmitting, isValid, isDirty });
	}, [errors, isSubmitting, isValid, isDirty]);

	return methods;
};
